import type { CustomFieldDefinition } from '../../features/orders/api/intakeApi'

interface IntakeFieldRendererProps {
  fields: CustomFieldDefinition[]
  values: Record<string, unknown>
  errors?: Record<string, string>
  onChange: (key: string, value: unknown) => void
}

const base = 'w-full rounded-md border px-3 py-2 text-sm focus:outline-none'
const ok = 'border-border focus:border-gray-500'
const bad = 'border-red-500 bg-red-50'

/**
 * Schema-driven intake fields (PE-1913) for the admin "create on behalf" forms.
 * Renders the merchant's effective field schema (from /field-config/:domain) into
 * controlled inputs. Mirrors the storefront FieldRenderer so rules stay aligned.
 */
export function IntakeFieldRenderer({ fields, values, errors = {}, onChange }: IntakeFieldRendererProps) {
  const ordered = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  return (
    <div className="flex flex-col gap-4">
      {ordered.map((f) => {
        const err = errors[f.key]
        const cls = `${base} ${err ? bad : ok}`
        const val = values[f.key]
        const options = f.validation?.options ?? []

        return (
          <div key={f.key}>
            {f.type !== 'boolean' && (
              <label className="mb-1 block text-sm font-medium text-foreground">
                {f.label}{f.required && <span className="text-red-500"> *</span>}
              </label>
            )}

            {f.type === 'textarea' && (
              <textarea className={cls} rows={3} value={(val as string) ?? ''} onChange={(e) => onChange(f.key, e.target.value)} />
            )}
            {f.type === 'number' && (
              <input type="number" className={cls} value={(val as number | string) ?? ''} onChange={(e) => onChange(f.key, e.target.value === '' ? '' : Number(e.target.value))} />
            )}
            {(f.type === 'text' || f.type === 'url' || f.type === 'file') && (
              <input type={f.type === 'text' ? 'text' : 'url'} placeholder={f.type !== 'text' ? 'https://…' : undefined} className={cls} value={(val as string) ?? ''} onChange={(e) => onChange(f.key, e.target.value)} />
            )}
            {f.type === 'date' && (
              <input type="date" className={cls} value={(val as string) ?? ''} onChange={(e) => onChange(f.key, e.target.value)} />
            )}
            {f.type === 'select' && (
              <select className={cls} value={(val as string) ?? ''} onChange={(e) => onChange(f.key, e.target.value)}>
                <option value="" disabled>Select…</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.type === 'multiselect' && (
              <div className="flex flex-wrap gap-3 pt-1">
                {options.map((o) => {
                  const arr = Array.isArray(val) ? (val as string[]) : []
                  return (
                    <label key={o} className="inline-flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={arr.includes(o)}
                        onChange={(e) => onChange(f.key, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))}
                      />
                      {o}
                    </label>
                  )
                })}
              </div>
            )}
            {f.type === 'boolean' && (
              <label className="inline-flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={Boolean(val)} onChange={(e) => onChange(f.key, e.target.checked)} />
                {f.label}
              </label>
            )}

            {f.helpText && <p className="mt-1 text-xs text-muted-foreground">{f.helpText}</p>}
            {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Validate the values against the schema; returns key→message errors (empty = valid).
 * Intentionally colocated with its renderer — pure helper, not a component; moving it
 * would fragment the intake module and fast-refresh impact is nil.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function validateIntake(fields: CustomFieldDefinition[], values: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const f of fields) {
    const v = values[f.key]
    const blank = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)
    if (blank) {
      if (f.required) errors[f.key] = `${f.label} is required`
      continue
    }
    const val = f.validation ?? {}
    if (f.type === 'number') {
      const n = typeof v === 'number' ? v : Number(v)
      if (!Number.isFinite(n)) errors[f.key] = `${f.label} must be a number`
      else if (val.min != null && n < val.min) errors[f.key] = `${f.label} must be ≥ ${val.min}`
      else if (val.max != null && n > val.max) errors[f.key] = `${f.label} must be ≤ ${val.max}`
    } else if ((f.type === 'text' || f.type === 'textarea' || f.type === 'url') && typeof v === 'string') {
      if (val.min != null && v.length < val.min) errors[f.key] = `${f.label} must be at least ${val.min} characters`
      else if (val.max != null && v.length > val.max) errors[f.key] = `${f.label} must be at most ${val.max} characters`
      else if (f.type === 'url' && !/^https?:\/\/.+/i.test(v)) errors[f.key] = `${f.label} must be a valid URL`
    } else if (f.type === 'select' && !options(f).includes(v as string)) {
      errors[f.key] = `${f.label} must be one of the options`
    }
  }
  return errors
}

function options(f: CustomFieldDefinition): string[] {
  return f.validation?.options ?? []
}
